// frontend/src/pages/Signup.js

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

import API from "../api/api";
import { setUser, setCredits } from "../store/userSlice";
import { clearActiveChat } from "../store/chatSlice";

import { DeptSelector, WelcomeCard } from "../components/Popups";
import { useLanguage, getTranslation } from "../utils/language";

import "../style/signup.css";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [language] = useLanguage();

  const user = useSelector((state) => state.user.user);
  const credits = useSelector((state) => state.user.credits);

  // ─── Ref-based guard (not state!) ─────────────────────────────────────────
  const signupInProgressRef = useRef(false);

  // Form states matching user screenshot exactly
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);

  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [signupStep, setSignupStep] = useState(0); // 0=form, 1=dept, 2=welcome
  const [signupUser, setSignupUser] = useState(null);

  // ── Redirect already-logged-in visitors away from /signup ──────────────────
  useEffect(() => {
    if (user && !signupInProgressRef.current) {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const handleGoogleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setError("");
        const res = await API.post("/auth/google", {
          accessToken: tokenResponse.access_token,
        });
        const { token, user: newUser } = res.data;
        localStorage.setItem("grgpt_token", token);
        localStorage.setItem("token", token);

        try {
          let stored = JSON.parse(localStorage.getItem("grgpt_saved_accounts") || "[]");
          const currentAccData = {
            id: newUser._id || "current-user",
            name: newUser.name || "User",
            email: newUser.email || "",
            avatar: newUser.profilePicture || newUser.avatar || "",
            token: token,
            userObj: newUser
          };
          const existingIndex = stored.findIndex(
            (acc) => (acc.email && newUser.email && acc.email.toLowerCase() === newUser.email.toLowerCase()) || (acc.id && acc.id === newUser._id)
          );
          if (existingIndex >= 0) {
            stored[existingIndex] = { ...stored[existingIndex], ...currentAccData };
          } else {
            stored.unshift(currentAccData);
          }
          localStorage.setItem("grgpt_saved_accounts", JSON.stringify(stored));
        } catch (e) {
          console.error("Error saving account to grgpt_saved_accounts:", e);
        }

        signupInProgressRef.current = true;
        setSignupUser(newUser);
        dispatch(setUser(newUser));
        dispatch(setCredits(newUser?.credits ?? 10));
        dispatch(clearActiveChat());

        const hasDepartments = Array.isArray(newUser?.departments) && newUser.departments.length > 0;
        if (hasDepartments) {
          signupInProgressRef.current = false;
          navigate("/", { replace: true });
        } else {
          setSignupStep(1);
        }
      } catch (err) {
        console.error("Google signup error:", err);
        setError(err.response?.data?.message || "Google sign-up failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    onError: (err) => {
      console.error("Google signup error:", err);
      setError("Google sign-up was cancelled or failed.");
    },
  });


  const handleSignup = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setInfoMessage("");

    // Validate inputs
    if (!name || name.trim().length === 0) {
      setError("Full Name is required");
      return;
    }
    if (name.trim().length > 100) {
      setError("Full Name cannot exceed 100 characters");
      return;
    }
    if (!/^[\p{L}\p{M}\s\-'.]+$/u.test(name.trim())) {
      setError("Full Name can only contain letters, spaces, hyphens, and apostrophes");
      return;
    }

    const trimmedEmail = email ? email.trim() : "";
    setEmail(trimmedEmail);
    if (!trimmedEmail || trimmedEmail.length > 254) {
      setError("Please enter a valid email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password.length > 128) {
      setError("Password cannot exceed 128 characters");
      return;
    }

    const pwdMissing = [];
    if (!/[A-Z]/.test(password)) pwdMissing.push("uppercase letter");
    if (!/[a-z]/.test(password)) pwdMissing.push("lowercase letter");
    if (!/[0-9]/.test(password)) pwdMissing.push("number");
    if (!/[^A-Za-z0-9]/.test(password)) pwdMissing.push("special character");

    if (pwdMissing.length > 0) {
      if (pwdMissing.length === 1) {
        setError(`Password must include at least one ${pwdMissing[0]}.`);
      } else {
        const last = pwdMissing.pop();
        setError(`Password must include at least one ${pwdMissing.join(", ")} and ${last}.`);
      }
      return;
    }

    if (!agree) {
      setError("You must agree to the Terms and Privacy Policy");
      return;
    }

    try {
      setLoading(true);

      const res = await API.post("/auth/signup", {
        name: name.trim(),
        email: trimmedEmail.toLowerCase(),
        password
      });

      if (res.data?.emailVerificationRequired) {
        signupInProgressRef.current = false;
        navigate("/login", {
          state: {
            email: trimmedEmail.toLowerCase(),
            emailVerificationRequired: true,
            devOtp: res.data.devOtp || ""
          }
        });
        return;
      }

      const { token, user: newUser } = res.data;
      localStorage.setItem("grgpt_token", token);
      localStorage.setItem("token", token);

      signupInProgressRef.current = true;
      setSignupUser(newUser);
      setSignupStep(1); // → show DeptSelector

      dispatch(setUser(newUser));
      dispatch(setCredits(newUser?.credits ?? 10));
      dispatch(clearActiveChat());

    } catch (err) {
      setError(err.response?.data?.message || err.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Department selection done ──────────────────────────────────────────────
  const handleDeptDone = async (selectedIds, selectedDepts = []) => {
    if (selectedDepts.length > 0) {
      localStorage.setItem(
        "grgpt_preferred_dept_names",
        JSON.stringify(selectedDepts.map((d) => d.name))
      );
    }

    setSignupStep(2); // → show WelcomeCard

    if (selectedIds?.length > 0) {
      try {
        await API.post("/user/preferences", { departments: selectedIds });
      } catch (err) {
        console.error("Dept save failed", err);
      }
    }
  };

  // ── Welcome card closed ────────────────────────────────────────────────────
  const handleWelcomeDone = () => {
    signupInProgressRef.current = false;

    const userCredits = signupUser?.credits ?? credits ?? 10;
    if (userCredits === 0) {
      navigate("/subscription");
    } else {
      navigate("/");
    }
  };

  return (
    <>
      {signupStep === 0 && (
        <div className="signup-page">

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: "420px" }}>

            {/* --- Desired Screenshot Heading & Subheading --- */}
            <h1 className="signup-main-title" style={{
              fontSize: "2.1rem",
              fontWeight: "800",
              color: "var(--color-text-primary)",
              textAlign: "center",
              margin: "0 0 4px",
              letterSpacing: "-0.5px",
              fontFamily: "'Outfit', 'Inter', sans-serif"
            }}>
              Create your account
            </h1>

            <p className="signup-subheading" style={{
              fontSize: "0.95rem",
              color: "var(--color-text-secondary)",
              textAlign: "center",
              margin: "0 0 24px",
              fontWeight: "400",
              fontFamily: "'Inter', sans-serif"
            }}>
              Join us today - it takes just a minute
            </p>

            <div className="signup-card" style={{ marginTop: 0 }}>

              {error && <div className="signup-error">{error}</div>}

              {infoMessage && (
                <div style={{
                  background: "rgba(16, 185, 129, 0.12)",
                  color: "#34d399",
                  border: "1px solid rgba(16, 185, 129, 0.2)",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  fontSize: "0.85rem",
                  marginBottom: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px"
                }}>
                  <span>{infoMessage}</span>
                </div>
              )}

              <form onSubmit={handleSignup} className="signup-form">

                {/* Full Name */}
                <label className="signup-label">Full Name</label>
                <input
                  type="text"
                  placeholder="john Deo"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                  className="signup-input"
                />

                {/* Email Address */}
                <label className="signup-label">Email address</label>
                <input
                  type="email"
                  placeholder="kanam90798@ixunbo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="signup-input"
                />

                {/* Password */}
                <label className="signup-label">Password</label>
                <div className="password-wrapper" style={{ width: "100%" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={getTranslation(language, "login", "password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="signup-input"
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setShowPassword((p) => !p)}
                    tabIndex={-1}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
                <span className="password-subtext" style={{
                  fontSize: "0.75rem",
                  color: "var(--color-text-muted)",
                  marginTop: "3px",
                  marginBottom: "8px",
                  display: "block",
                  lineHeight: "1.3"
                }}>
                  Use 8 or more characters with a mix of letters, numbers & symbols
                </span>

                {/* Checkbox agreement matching screenshot */}
                <div className="terms-checkbox" style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginTop: "16px",
                  marginBottom: "8px"
                }}>
                  <input
                    type="checkbox"
                    id="terms-check"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    disabled={loading}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "1px",
                      cursor: "pointer"
                    }}
                  />
                  <label htmlFor="terms-check" style={{
                    fontSize: "0.85rem",
                    color: "var(--color-text-secondary)",
                    cursor: "pointer",
                    fontWeight: "400",
                    margin: 0,
                    lineHeight: "1.4"
                  }}>
                    I agree to the <Link to="/terms" target="_blank" style={{ color: "var(--color-primary)", fontWeight: "600", textDecoration: "none" }}>Terms</Link> and <Link to="/privacy" target="_blank" style={{ color: "var(--color-primary)", fontWeight: "600", textDecoration: "none" }}>Privacy Policy</Link>
                  </label>
                </div>

                {/* Action button */}
                <button
                  type="submit"
                  className="signup-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Sign Up"}
                </button>

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "12px 0 6px",
                  color: "var(--color-text-secondary, #94a3b8)",
                  fontSize: "0.8rem",
                  textAlign: "center"
                }}>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border, #e2e8f0)" }} />
                  <span style={{ padding: "0 10px" }}>or</span>
                  <div style={{ flex: 1, height: "1px", backgroundColor: "var(--color-border, #e2e8f0)" }} />
                </div>

                <button
                  type="button"
                  onClick={() => handleGoogleSignup()}
                  disabled={loading}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    width: "100%",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    border: "1px solid var(--color-border, #e2e8f0)",
                    backgroundColor: "#ffffff",
                    color: "#334155",
                    fontWeight: "600",
                    fontSize: "0.88rem",
                    cursor: "pointer",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease"
                  }}
                >
                  <GoogleIcon />
                  <span>Continue with Google</span>
                </button>
              </form>

              <div className="signup-footer">
                <span>Already have an account?</span>
                <Link to="/login">Login</Link>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* ── STEP 1: Dept Selector ── */}
      {signupStep === 1 && (
        <DeptSelector
          userName={name || signupUser?.name || "User"}
          onDone={handleDeptDone}
          onClose={() => {
            signupInProgressRef.current = false;
            setSignupStep(0);
            navigate("/");
          }}
        />
      )}

      {/* ── STEP 2: Welcome Card ── */}
      {signupStep === 2 && (
        <WelcomeCard
          userName={name || signupUser?.name || "User"}
          onClose={handleWelcomeDone}
          freeChatsLeft={signupUser?.credits ?? credits ?? 10}
        />
      )}
    </>
  );
}